/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormControl, FormHelperText, InputLabel, OutlinedInput } from '@mui/material';

import { KubernetesIdentifier } from 'shared';

import { Namespaces } from '../../../i18n';
import { CreateClusterState } from '../../../store/createCluster/types';
import { useFormStyles } from '../../../styles/form';
import { ApiValidationErrors, ApiValidationResetFieldError } from '../../../utils/useApiValidation';
import { CreateClusterValues } from '../CreateClusterOverlay.types';
import { SelectOrganizationProject } from './SelectOrganizationProject/SelectOrganizationProject';

type CreateClusterStep1Props<Values> = FormikProps<Values> & {
  createCluster: CreateClusterState;
  warnInaccessibleInstance?: boolean;
  apiErrors: ApiValidationErrors;
  resetApiError: ApiValidationResetFieldError;
};

export function CreateClusterStepInfo<Values extends CreateClusterValues>(props: CreateClusterStep1Props<Values>) {
  const { apiErrors, resetApiError, createCluster, ...formikProps } = props;
  const { errors, values, handleChange, touched, handleBlur } = formikProps;
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.CreateCluster);

  const hasError = (field: keyof CreateClusterValues, skipTouched: boolean = false) => {
    return (skipTouched || touched[field]) && !!(errors[field] || apiErrors[field]);
  };

  const getError = (field: keyof CreateClusterValues) => {
    const error = errors[field] || apiErrors[field];
    if (error) {
      return t(`validation.${field}.${error}`, {
        minLength: KubernetesIdentifier.MinLength,
        maxLength: KubernetesIdentifier.MaxLength,
      });
    }
    return undefined;
  };

  const onChange = (name: keyof CreateClusterValues, event: React.ChangeEvent) => {
    handleChange(event);
    resetApiError(name);
  };

  const onNameChange = onChange.bind(null, 'name');

  const isNameAutoFocus = !!createCluster.organizationId && !!createCluster.projectId;

  return (
    <>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('projectId')}
        variant="outlined"
      >
        <SelectOrganizationProject {...formikProps} />
        {hasError('projectId') && <FormHelperText>{getError('projectId')}</FormHelperText>}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthDefault)}
        error={hasError('name')}
        variant="outlined"
      >
        <InputLabel htmlFor="name">{t('labels.name')}</InputLabel>
        <OutlinedInput
          id="name"
          name="name"
          value={values.name}
          onChange={onNameChange}
          label={t('labels.name')}
          onBlur={handleBlur}
          autoFocus={isNameAutoFocus}
          data-cy="create-cluster-name-field"
        />
        {hasError('name') && <FormHelperText>{getError('name')}</FormHelperText>}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('description')}
        variant="outlined"
      >
        <InputLabel htmlFor="description">{t('labels.description')}</InputLabel>
        <OutlinedInput
          id="description"
          name="description"
          value={values.description}
          onChange={handleChange}
          label={t('labels.description')}
          fullWidth
          multiline
          onBlur={handleBlur}
          data-cy="create-cluster-description-field"
        />
        {hasError('description') && <FormHelperText>{getError('description')}</FormHelperText>}
      </FormControl>
    </>
  );
}
